import { useState, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { showCenteredCheckoutError } from '../lib/toastHelpers';
import { useAuthStore } from './useAuth';

// ── Constants ─────────────────────────────────────────────────────────
export const LATE_CHECKIN_THRESHOLD = 10;   // minutes past startAt
export const LATE_CHECKOUT_THRESHOLD = 120; // minutes past endAt

// ── Minimal shape the hook needs (works with both full Appointment and slim DockDrawer occupant) ──
export interface ActionTarget {
    id: string;
    startAt?: string;
    endAt?: string;
    dockId?: string | null;
    dock_id?: string | null; // Legacy field — some API responses use snake_case
}

/** Pure check: does this target have a dock assigned? Handles both naming conventions. */
export function hasDockAssigned(appt: ActionTarget): boolean {
    return !!(appt.dockId || appt.dock_id);
}

// ── Pure helpers (exported for testing) ───────────────────────────────

/** Parse `startAt` and return epoch ms, or throw a user-friendly error. */
export function validateStartAt(startAt: string | undefined | null): number {
    if (!startAt) throw new Error('Appointment has no scheduled start time.');
    const ms = new Date(startAt).getTime();
    if (!Number.isFinite(ms)) throw new Error('Invalid appointment time — cannot determine lateness.');
    return ms;
}

/** Minutes elapsed since `startAt`. Throws if `startAt` is unparseable. */
export function getMinutesPastStart(startAt: string | undefined | null): number {
    return (Date.now() - validateStartAt(startAt)) / 60_000;
}

/** Minutes elapsed since `endAt`, or null if `endAt` is missing/invalid. */
export function getMinutesPastEnd(endAt: string | undefined | null): number | null {
    if (!endAt) return null;
    const ms = new Date(endAt).getTime();
    if (!Number.isFinite(ms)) return null;
    return (Date.now() - ms) / 60_000;
}

/** Build the JSON body for a check-in request. */
export function buildCheckinPayload(opts?: { checkInTime?: string }): Record<string, string> {
    const body: Record<string, string> = {};
    if (opts?.checkInTime) body.checkInTime = opts.checkInTime;
    return body;
}

/** Build the JSON body for a checkout request. */
export function buildCheckoutPayload(opts?: any): Record<string, string> {
    const body: Record<string, string> = {};
    if (opts) {
        Object.assign(body, opts);
    }
    return body;
}

// ── Types ─────────────────────────────────────────────────────────────

type ActionKind = 'checkin' | 'checkout';

interface LateModalState {
    appt: ActionTarget;
    action: ActionKind;
}

interface UseAppointmentActionsOpts {
    /** Called after a successful API response so the consumer can refresh data. */
    onSuccess?: () => void;
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useAppointmentActions({ onSuccess }: UseAppointmentActionsOpts = {}) {
    const { token } = useAuthStore();

    // Loading state keyed by appointment id
    const [actionLoading, setActionLoading] = useState<Record<string, ActionKind | null>>({});

    // Late-override modal state
    const [lateModal, setLateModal] = useState<LateModalState | null>(null);

    // ── Internal API call ─────────────────────────────────────────────

    const executeAction = useCallback(async (
        appt: ActionTarget,
        action: ActionKind,
        overrideTime?: string,
    ) => {
        if (!token) return;

        setActionLoading(prev => ({ ...prev, [appt.id]: action }));

        try {
            const endpoint = action === 'checkin'
                ? `/api/appointments/${appt.id}/checkin`
                : `/api/appointments/${appt.id}/checkout`;

            const method = 'POST';

            let overridePayload: any = {};
            if (overrideTime) {
                // If the time comes from LateTimeModal it is a local string "YYYY-MM-DDTHH:mm"
                if (overrideTime.includes('T') && !overrideTime.endsWith('Z')) {
                    const [d, t] = overrideTime.split('T');
                    overridePayload = { actualDate: d, actualTime: t };
                } else {
                    // Backwards compatibility if ISO string is passed somehow
                    overridePayload = action === 'checkin' ? { checkInTime: overrideTime } : { checkOutTime: overrideTime };
                }
            }

            const body = action === 'checkin'
                ? { status: 'checked_in', ...overridePayload }
                : buildCheckoutPayload(Object.keys(overridePayload).length > 0 ? overridePayload : undefined);

            if (import.meta.env.DEV && overrideTime) {
                console.log(`[AppointmentActions] ${action} override:`, { id: appt.id, overrideTime });
            }

            const response = await fetch(endpoint, {
                method: method,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                // Backend late-enforcement fallback (kept for backwards compat)
                if (errData.error === 'LATE_CHECKIN_REQUIRES_TIME') {
                    setLateModal({ appt, action: 'checkin' });
                    return;
                }
                if (errData.error === 'LATE_CHECKOUT_REQUIRES_TIME') {
                    setLateModal({ appt, action: 'checkout' });
                    return;
                }
                throw new Error(errData.error || errData.message || `${action} failed`);
            }

            const data = await response.json().catch(() => ({}));
            const label = action === 'checkin' ? 'Arrival' : 'Check-out';
            const detail = action === 'checkin'
                ? (data.lateStatus ? ` ${data.lateStatus}` : '')
                : (data.dwellTime ? ` Dwell time: ${data.dwellTime}` : '');

            toast.success(`${label} successful!${detail}`);
            onSuccess?.();
        } catch (err: any) {
            const label = action === 'checkin' ? 'check in' : 'check out';
                        if (action === 'checkout') {
                showCenteredCheckoutError(`Failed to ${label}: ${err.message}`);
            } else {
                toast.error(`Failed to ${label}: ${err.message}`);
            }
        } finally {
            setActionLoading(prev => ({ ...prev, [appt.id]: null }));
        }
    }, [token, onSuccess]);

    // ── Public: checkIn ───────────────────────────────────────────────

    const checkIn = useCallback((appt: ActionTarget, opts?: { checkInTime?: string }) => {
        // Yard arrival — no dock required. Uses PATCH /status to move approved → checked_in.
        // Client-side late detection (show modal if >10 min past start)
        if (!opts?.checkInTime) {
            try {
                const minutesPast = getMinutesPastStart(appt.startAt);
                if (minutesPast > LATE_CHECKIN_THRESHOLD) {
                    setLateModal({ appt, action: 'checkin' });
                    return;
                }
            } catch {
                toast.error('Invalid appointment time — cannot determine lateness.');
                return;
            }
        }
        executeAction(appt, 'checkin', opts?.checkInTime);
    }, [executeAction]);

    // ── Public: checkOut ──────────────────────────────────────────────

    const checkOut = useCallback((appt: ActionTarget, opts?: { checkOutTime?: string }) => {
        // Client-side late detection (show modal if >2 h past endAt)
        if (!opts?.checkOutTime) {
            const minutesPast = getMinutesPastEnd(appt.endAt);
            if (minutesPast !== null && minutesPast > LATE_CHECKOUT_THRESHOLD) {
                setLateModal({ appt, action: 'checkout' });
                return;
            }
        }
        executeAction(appt, 'checkout', opts?.checkOutTime);
    }, [executeAction]);

    // ── Public: dispatchToDock (checked_in → in_progress) ──
    // Checked-in trucks already have a dock assigned — no /approve call needed.
    // Just transition the status to in_progress.

    const dispatchToDock = useCallback(async (appt: ActionTarget) => {
        if (!token) return;

        try {
            const res = await fetch(`/api/appointments/${appt.id}/status`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: 'in_progress',
                    ...(appt.dockId ? { dockId: appt.dockId } : {}),
                }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Dispatch failed');
            }

            toast.success('Truck dispatched to dock!');
            onSuccess?.();
        } catch (err: any) {
            toast.error(`Dispatch failed: ${err.message}`);
        }
    }, [token, onSuccess]);

    // ── isActionLoading helper ────────────────────────────────────────

    const isActionLoading = useCallback((apptId: string) => !!actionLoading[apptId], [actionLoading]);

    // ── LateTimeModal props (ready to spread) ─────────────────────────

    const lateModalProps = useMemo(() => ({
        open: !!lateModal,
        isSubmitting: lateModal ? !!actionLoading[lateModal.appt.id] : false,
        title: lateModal?.action === 'checkout'
            ? 'Late Check-Out — Confirm Actual Time'
            : 'Late Check-In — Confirm Actual Time',
        description: lateModal?.action === 'checkout'
            ? 'This check-out is more than 2 hours past the scheduled end. Please confirm the actual departure time.'
            : 'This check-in is more than 10 min past the scheduled start. Please confirm the actual arrival time.',
        defaultTime: new Date().toISOString(),
        onConfirm: (iso: string) => {
            const state = lateModal;
            setLateModal(null);
            if (!state) return;
            if (state.action === 'checkin') {
                executeAction(state.appt, 'checkin', iso);
            } else {
                executeAction(state.appt, 'checkout', iso);
            }
        },
        onCancel: () => setLateModal(null),
    }), [lateModal, actionLoading, executeAction]);

    return { checkIn, checkOut, dispatchToDock, isActionLoading, lateModalProps };
}


